import { useEffect } from 'react';

/**
 * Lightweight per-page SEO helper.
 * Sets document.title, meta[name=description], canonical, and optionally
 * injects a JSON-LD <script> for structured data.
 *
 * Usage:
 *   <Seo
 *     title="Book an Appointment | Bombay Bespoke Fashion House"
 *     description="..."
 *     canonical="https://bombaybespokefashion.com/book-appointment"
 *     jsonLd={{ "@context": "https://schema.org", "@type": "FAQPage", ... }}
 *   />
 */
export default function Seo({ title, description, canonical, jsonLd, ogImage }) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attr, value) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement('meta');
        const [_, name] = selector.match(/\[(name|property)="([^"]+)"\]/) || [];
        const [k, v] = selector.match(/\[([^=]+)="([^"]+)"\]/)?.slice(1) || [];
        if (k && v) tag.setAttribute(k, v);
        document.head.appendChild(tag);
      }
      tag.setAttribute(attr, value);
    };

    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (ogImage) {
      setMeta('meta[property="og:image"]', 'content', ogImage);
      setMeta('meta[name="twitter:image"]', 'content', ogImage);
    }

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonical);
      setMeta('meta[property="og:url"]', 'content', canonical);
    }

    // JSON-LD injection (one per route via id)
    const SCRIPT_ID = 'page-jsonld';
    document.head.querySelectorAll(`script#${SCRIPT_ID}`).forEach((el) => el.remove());
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = SCRIPT_ID;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      document.head.querySelectorAll(`script#${SCRIPT_ID}`).forEach((el) => el.remove());
    };
  }, [title, description, canonical, ogImage, jsonLd]);

  return null;
}
